const MICROLINK_API = 'https://api.microlink.io';
const MICROLINK_PRO_API = 'https://pro.microlink.io';

export interface MicrolinkMedia {
  url: string;
  type?: string;
  size?: number;
  height?: number;
  width?: number;
  size_pretty?: string;
}

export interface MicrolinkData {
  title?: string;
  description?: string;
  lang?: string;
  author?: string | null;
  publisher?: string | null;
  date?: string;
  image?: MicrolinkMedia;
  logo?: MicrolinkMedia;
  video?: MicrolinkMedia;
  url?: string;
  [key: string]: unknown;
}

export interface MicrolinkResponse {
  status: 'success' | 'fail';
  data?: MicrolinkData;
  message?: string;
  code?: string;
}

export const normalizeTargetUrl = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const isValidHttpUrl = (input: string): boolean => {
  try {
    const url = new URL(normalizeTargetUrl(input));
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const fetchMicrolinkMetadata = async (
  targetUrl: string,
  apiKey?: string,
): Promise<MicrolinkResponse> => {
  const url = normalizeTargetUrl(targetUrl);
  const endpoint = apiKey ? MICROLINK_PRO_API : MICROLINK_API;
  const requestUrl = `${endpoint}?${new URLSearchParams({ url }).toString()}`;

  const headers: HeadersInit = {};
  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  const response = await fetch(requestUrl, { headers });

  if (!response.ok) {
    throw new Error(`Microlink API 오류 (${response.status})`);
  }

  const payload = (await response.json()) as MicrolinkResponse;

  if (payload.status === 'fail') {
    const detail =
      typeof payload.data?.url === 'string'
        ? payload.data.url
        : payload.message;
    throw new Error(detail ?? '메타데이터를 가져오지 못했습니다.');
  }

  return payload;
};

export const getMediaUrl = (
  media?: MicrolinkMedia | string | null,
): string | undefined => {
  if (!media) return undefined;
  if (typeof media === 'string') return media;
  return media.url;
};

const getFilenameFromUrl = (fileUrl: string) => {
  try {
    const pathname = new URL(fileUrl).pathname;
    const filename = pathname.split('/').pop();
    if (filename && /\.[a-z0-9]+$/i.test(filename)) {
      return filename.split('?')[0];
    }
  } catch {
    // ignore invalid URL
  }

  return `metadata-image-${Date.now()}.jpg`;
};

export const downloadImageFromUrl = async (
  fileUrl: string,
  filename?: string,
) => {
  const downloadName = filename ?? getFilenameFromUrl(fileUrl);

  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error('이미지 다운로드에 실패했습니다.');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = downloadName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
    return;
  } catch {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = downloadName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
};
